import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import isNumber from 'lodash/isNumber';
import noop from 'lodash/noop';
import { Theme } from '@guestyci/foundation/enums/enums';
import CarouselCustomArrow from "./CarouselCustomArrow";
import Bullet from "../Bullet/Bullet";

var Carousel = function Carousel(_ref) {
  var children = _ref.children,
      dots = _ref.dots,
      arrows = _ref.arrows,
      autoplaySpeed = _ref.autoplaySpeed,
      autoplay = _ref.autoplay,
      adaptiveHeight = _ref.adaptiveHeight,
      fade = _ref.fade,
      beforeChange = _ref.beforeChange,
      infinite = _ref.infinite,
      showArrowsOnHover = _ref.showArrowsOnHover,
      activeSlide = _ref.activeSlide,
      draggable = _ref.draggable,
      onChange = _ref.onChange,
      className = _ref.className,
      arrowTheme = _ref.arrowTheme,
      arrowsFade = _ref.arrowsFade,
      style = _ref.style,
      accessibility = _ref.accessibility;
  var sliderRef = useRef(null);
  var afterChange = useCallback(function (slideIndex) {
    onChange(slideIndex);
  });
  var settings = {
    dots: dots,
    arrows: arrows,
    autoplaySpeed: autoplaySpeed,
    autoplay: autoplay,
    fade: fade,
    beforeChange: beforeChange,
    infinite: infinite,
    draggable: draggable,
    afterChange: afterChange,
    className: className,
    style: style,
    accessibility: accessibility,
    adaptiveHeight: adaptiveHeight,
    initialSlide: activeSlide !== undefined ? activeSlide : 0,
    prevArrow: /*#__PURE__*/React.createElement(CarouselCustomArrow, {
      fade: arrowsFade,
      theme: arrowTheme,
      direction: "left",
      showArrowsOnHover: showArrowsOnHover
    }),
    nextArrow: /*#__PURE__*/React.createElement(CarouselCustomArrow, {
      fade: arrowsFade,
      theme: arrowTheme,
      direction: "right",
      showArrowsOnHover: showArrowsOnHover
    }),
    dotsClass: 'absolute text-align-center w-fill carousel-dots',
    customPaging: function customPaging() {
      return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Bullet, {
        color: "white",
        className: "clickable carousel-bullet"
      }));
    }
  };
  useEffect(function () {
    if (isNumber(activeSlide)) {
      sliderRef.current.slickGoTo(activeSlide);
    }
  }, [activeSlide, sliderRef.current]);
  return /*#__PURE__*/React.createElement("div", {
    className: "d-flex w-fill",
    "data-qa": "carousel"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-grow-1 width-0 carousel-component"
  }, /*#__PURE__*/React.createElement(Slider, _extends({
    ref: sliderRef
  }, settings), children)));
};

Carousel.defaultProps = {
  accessibility: true,
  dots: true,
  arrows: false,
  showArrowsOnHover: false,
  infinite: false,
  draggable: true,
  onChange: noop,
  className: '',
  arrowsFade: false,
  arrowTheme: Theme.Dark,
  style: {}
};
Carousel.propTypes = {
  /** indicates if to display dots */
  dots: PropTypes.bool,

  /** indicates if to display arrows */
  arrows: PropTypes.bool,

  /** indicates if to display fade around the arrows */
  arrowsFade: PropTypes.bool,

  /** Display arrows only when hovering */
  showArrowsOnHover: PropTypes.bool,

  /** arrow's theme */
  arrowTheme: PropTypes.oneOf([Theme.Light, Theme.Dark]),

  /** Infinitely wrap around contents */
  infinite: PropTypes.bool,

  /** Enable scrollable via dragging on desktop */
  draggable: PropTypes.bool,

  /** Slide index to show */
  activeSlide: PropTypes.number,

  /** Additional class name */
  className: PropTypes.string,

  /** Additional style */
  style: PropTypes.shape(),

  /** Delay between each auto scroll (in milliseconds) */
  autoplaySpeed: PropTypes.number,

  /** Enables auto scroll */
  autoplay: PropTypes.bool,

  /** Adjust the slide's height automatically */
  adaptiveHeight: PropTypes.bool,

  /** set Fade animation type */
  fade: PropTypes.bool,

  /** callback before slide changes */
  beforeChange: PropTypes.func,

  /** callback when slide changes through arrows/dots */
  onChange: PropTypes.func,

  /** The item to render inside the carousel */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,

  /** Enable tabbing and arrow key navigation */
  accessibility: PropTypes.bool
};
export default Carousel;